import { useState, useCallback, useMemo } from 'react';
import { LAYOUT_MODES } from '../constants';

/**
 * Custom hook for EntityPanelSelector state management
 * Handles search, filtering, grouping, and selection logic
 */
function useEntityPanelState({
  mode,
  availableItems,
  selectedItemIds,
  onSelectionChange,
  middlePanelItems,
  categories,
  selectedItemsWithCategory,
  valueField,
}) {
  // Search state
  const [leftSearchTerm, setLeftSearchTerm] = useState('');
  const [middleSearchTerm, setMiddleSearchTerm] = useState('');
  const [selectedSearchTerm, setSelectedSearchTerm] = useState('');

  // Unique instance ID to prevent checkbox ID collisions
  const [instanceId] = useState(() => Math.random().toString(36).substring(2, 9));

  // Helper to get item value
  const getItemValue = useCallback((item) => item[valueField] ?? item.id, [valueField]);

  // Determine what to show in left panel based on mode
  const leftPanelItems = useMemo(() => {
    if (mode === LAYOUT_MODES.THREE_COLUMNS_WITH_FILTER) {
      return categories || [];
    }
    return availableItems;
  }, [mode, categories, availableItems]);

  // Filter left panel items
  const filteredLeftPanelItems = useMemo(() => {
    let items = leftPanelItems;
    if (leftSearchTerm) {
      items = items.filter((item) =>
        item.name?.toLowerCase().includes(leftSearchTerm.toLowerCase())
      );
    }
    return items;
  }, [leftPanelItems, leftSearchTerm]);

  // Filter middle panel items (only for 3-column mode)
  const filteredMiddlePanelItems = useMemo(() => {
    if (mode !== LAYOUT_MODES.THREE_COLUMNS_WITH_FILTER) return [];
    if (!middlePanelItems) return [];
    if (!middleSearchTerm) return middlePanelItems;
    return middlePanelItems.filter((item) =>
      item.name?.toLowerCase().includes(middleSearchTerm.toLowerCase())
    );
  }, [mode, middlePanelItems, middleSearchTerm]);

  // Group middle panel items by hostName
  const groupedMiddlePanelItems = useMemo(() => {
    if (mode !== LAYOUT_MODES.THREE_COLUMNS_WITH_FILTER) return null;
    if (!middlePanelItems || middlePanelItems.length === 0) return null;
    
    const hasGroupingField = middlePanelItems.some((item) => item.hostName);
    if (!hasGroupingField) return null;

    const groups = {};
    filteredMiddlePanelItems.forEach((item) => {
      const groupKey = item.hostName || 'Unknown';
      if (!groups[groupKey]) {
        groups[groupKey] = {
          groupName: groupKey,
          items: [],
        };
      }
      groups[groupKey].items.push(item);
    });

    return Object.values(groups).sort((a, b) => a.groupName.localeCompare(b.groupName));
  }, [mode, middlePanelItems, filteredMiddlePanelItems]);

  // Get selected items list
  const selectedItemsList = useMemo(() => {
    const sourceItems = mode === LAYOUT_MODES.THREE_COLUMNS_WITH_FILTER
      ? middlePanelItems || []
      : availableItems;
    return sourceItems.filter((item) => selectedItemIds.includes(getItemValue(item)));
  }, [mode, availableItems, middlePanelItems, selectedItemIds, getItemValue]);

  // Filter selected items
  const filteredSelectedItems = useMemo(() => {
    if (!selectedSearchTerm) return selectedItemsList;
    return selectedItemsList.filter((item) =>
      item.name?.toLowerCase().includes(selectedSearchTerm.toLowerCase())
    );
  }, [selectedItemsList, selectedSearchTerm]);

  // Group selected items by category
  const groupedSelectedItems = useMemo(() => {
    if (mode !== LAYOUT_MODES.THREE_COLUMNS_WITH_FILTER || !selectedItemsWithCategory) {
      return null;
    }

    const groups = {};
    selectedItemIds.forEach((itemId) => {
      const itemData = selectedItemsWithCategory[itemId];
      if (itemData) {
        const { categoryId, categoryName, item } = itemData;
        if (selectedSearchTerm && !item.name?.toLowerCase().includes(selectedSearchTerm.toLowerCase())) {
          return;
        }
        if (!groups[categoryId]) {
          groups[categoryId] = {
            categoryId,
            categoryName,
            items: [],
          };
        }
        groups[categoryId].items.push(item);
      }
    });

    return Object.values(groups);
  }, [mode, selectedItemsWithCategory, selectedItemIds, selectedSearchTerm]);

  // Handle item toggle
  const handleItemToggle = useCallback((itemValue) => {
    const isSelected = selectedItemIds.includes(itemValue);
    const newSelection = isSelected
      ? selectedItemIds.filter((id) => id !== itemValue)
      : [...selectedItemIds, itemValue];
    onSelectionChange(newSelection);
  }, [selectedItemIds, onSelectionChange]);

  // Handle select all
  const handleSelectAll = useCallback(() => {
    const itemsToSelect = mode === LAYOUT_MODES.THREE_COLUMNS_WITH_FILTER
      ? filteredMiddlePanelItems
      : filteredLeftPanelItems;
    const allIds = itemsToSelect.map((item) => getItemValue(item));
    const allSelected = allIds.every((id) => selectedItemIds.includes(id));
    
    if (allSelected) {
      onSelectionChange(selectedItemIds.filter((id) => !allIds.includes(id)));
    } else {
      const newSelection = [...new Set([...selectedItemIds, ...allIds])];
      onSelectionChange(newSelection);
    }
  }, [mode, filteredMiddlePanelItems, filteredLeftPanelItems, selectedItemIds, onSelectionChange, getItemValue]);

  // Handle clear all
  const handleClearAll = useCallback(() => {
    onSelectionChange([]);
  }, [onSelectionChange]);

  // Handle remove single item
  const handleRemoveItem = useCallback((itemValue) => {
    onSelectionChange(selectedItemIds.filter((id) => id !== itemValue));
  }, [selectedItemIds, onSelectionChange]);

  return {
    // State
    instanceId,
    leftSearchTerm,
    setLeftSearchTerm,
    middleSearchTerm,
    setMiddleSearchTerm,
    selectedSearchTerm,
    setSelectedSearchTerm,
    // Computed
    getItemValue,
    filteredLeftPanelItems,
    filteredMiddlePanelItems,
    groupedMiddlePanelItems,
    filteredSelectedItems,
    groupedSelectedItems,
    // Handlers
    handleItemToggle,
    handleSelectAll,
    handleClearAll,
    handleRemoveItem,
  };
}

export default useEntityPanelState;

